// Talk to the tweetvia Chrome extension from the web app.
//
// Production needs the extension's stable ID (from chrome.google.com/webstore/...).
// During dev each loaded extension gets a different ID, so we fall back to
// reading it from VITE_EXTENSION_ID — set it in apps/web/.env.local during dev.

declare global {
  interface Window { chrome?: any }
}

export interface ScrapedProfile {
  blocked: boolean;
  handle?: string;
  displayName?: string;
  bio?: string;
  profileImageUrl?: string;
  bannerImageUrl?: string;
  pinnedTweetText?: string;
  followers?: number;
  following?: number;
  verified?: boolean;
  tweets: string[];
  diagnostics?: { tweetsFound: number; scrolls: number; durationMs: number; reason?: string };
}

const EXT_ID_KEY = 'tv_extension_id';

export function getExtensionId(): string | null {
  // 1. Env var wins (dev mode, where ID is unstable across loads).
  const envId = (import.meta.env.VITE_EXTENSION_ID as string | undefined)?.trim();
  if (envId) return envId;
  // 2. localStorage override (lets a user paste their dev extension ID in Settings later).
  return localStorage.getItem(EXT_ID_KEY);
}

export function setExtensionId(id: string): void {
  localStorage.setItem(EXT_ID_KEY, id.trim());
}

// Listen for the handshake the extension's content script broadcasts via window.postMessage
// the moment the page loads. This is how the web app discovers the extension's ID on
// dev-loaded installs (where the ID isn't known at build time).
let handshakeBooted = false;
export function bootExtensionHandshake(): void {
  if (handshakeBooted || typeof window === 'undefined') return;
  handshakeBooted = true;
  window.addEventListener('message', (event) => {
    if (event.source !== window) return;
    const msg = event.data;
    if (msg?.source === 'tweetvia-extension' && msg?.type === 'handshake' && typeof msg.id === 'string') {
      const current = localStorage.getItem(EXT_ID_KEY);
      if (current !== msg.id) localStorage.setItem(EXT_ID_KEY, msg.id);
    }
  });
  // Nudge in case the content script's first announcement was before our listener attached.
  window.postMessage({ source: 'tweetvia-web', type: 'ping' }, '*');
}

function sendMessageAsync<T>(extId: string, payload: unknown, timeoutMs = 60_000): Promise<T> {
  return new Promise((resolve, reject) => {
    if (!window.chrome?.runtime?.sendMessage) {
      reject(new Error('chrome.runtime.sendMessage not available — open in Chrome.'));
      return;
    }
    let settled = false;
    const t = setTimeout(() => { if (!settled) { settled = true; reject(new Error('extension_timeout')); } }, timeoutMs);
    try {
      window.chrome.runtime.sendMessage(extId, payload, (response: any) => {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        if (window.chrome.runtime.lastError) {
          reject(new Error(window.chrome.runtime.lastError.message));
        } else {
          resolve(response as T);
        }
      });
    } catch (err) {
      settled = true;
      clearTimeout(t);
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}

/** Detect whether the extension is installed and responding. */
export async function pingExtension(): Promise<{ installed: boolean; version?: string }> {
  const id = getExtensionId();
  if (!id) return { installed: false };
  try {
    const r = await sendMessageAsync<{ ok?: boolean; version?: string }>(id, { type: 'tweetvia:ping' }, 4000);
    return { installed: !!r?.ok, version: r?.version };
  } catch {
    return { installed: false };
  }
}

/** Ask the extension to scrape x.com/<handle> in a new tab. */
export async function scrapeProfileViaExtension(handle: string): Promise<ScrapedProfile> {
  const id = getExtensionId();
  if (!id) throw new Error('extension_not_configured');
  const cleaned = handle.replace(/^@/, '');
  const r = await sendMessageAsync<ScrapedProfile & { error?: string }>(id, { type: 'tweetvia:scrapeProfile', handle: cleaned });
  if (!r || (r as any).error) throw new Error((r as any)?.error ?? 'extension_error');
  return r;
}

export interface ScrapedSearchResult {
  query: string;
  blocked: boolean;
  tweets: Array<{
    tweetId: string; text: string; authorHandle?: string; authorName?: string;
    authorVerified?: boolean; likeCount?: number; replyCount?: number; retweetCount?: number;
    createdAt?: string; tweetUrl?: string;
  }>;
}

/** Ask the extension to scrape x.com/search?q=<query>. */
export async function scanTopicViaExtension(query: string): Promise<ScrapedSearchResult> {
  const id = getExtensionId();
  if (!id) throw new Error('extension_not_configured');
  const r = await sendMessageAsync<ScrapedSearchResult & { error?: string }>(id, { type: 'tweetvia:scanTopic', query });
  if (!r || (r as any).error) throw new Error((r as any)?.error ?? 'extension_error');
  return r;
}

/**
 * Pre-register a query → monitorId mapping with the extension. Used right before we
 * open x.com/search in a new tab so the extension's content script knows to passively
 * capture tweets and attribute them to this monitor.
 *
 * Returns `false` if the extension isn't installed (no-op caller can still open the
 * link; just no capture will happen).
 */
export async function registerPassiveCapture(query: string, monitorId: string): Promise<boolean> {
  const id = getExtensionId();
  if (!id) return false;
  try {
    const r = await sendMessageAsync<{ ok?: boolean; error?: string }>(id, { type: 'tweetvia:registerCapture', query, monitorId }, 4000);
    return !!r?.ok;
  } catch { return false; }
}

/**
 * Push a fresh Firebase ID token to the extension. Firebase tokens expire after 1 hour,
 * so the extension's stored token goes stale fast — without this, the background's
 * /v1/radar/scan-scraped POSTs start returning 401 and tweets never reach the feed.
 *
 * Call this on every load (after auth resolves) so the extension always has a usable
 * token. The web app re-fetches a fresh token via Firebase SDK before sending.
 */
export async function refreshExtensionToken(token: string, apiBaseUrl: string): Promise<boolean> {
  const id = getExtensionId();
  if (!id) return false;
  try {
    const r = await sendMessageAsync<{ ok?: boolean }>(id, { type: 'tweetvia:setToken', token, apiBaseUrl }, 4000);
    return !!r?.ok;
  } catch { return false; }
}

/**
 * Save an image to the user's local Downloads/tweetvia/ folder via the extension. We
 * route through the extension because chrome.downloads bypasses CORS — browser-only
 * <a download> with a cross-origin URL often fails (the file ends up named after the
 * URL with no extension, or the download is blocked).
 *
 * `hint` becomes the filename slug. The extension adds a timestamp and the inferred
 * extension. Returns the saved filename ("tweetvia/<name>.jpg") on success, or null.
 */
export async function downloadImageViaExtension(url: string, hint?: string): Promise<string | null> {
  const id = getExtensionId();
  if (!id) return null;
  const inferredExt = (url.match(/\.(jpe?g|png|webp|gif|avif)(\?|#|$)/i)?.[1] ?? 'jpg').toLowerCase().replace('jpeg', 'jpg');
  const slug = (hint || 'image').toLowerCase().replace(/[^a-z0-9-_]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'image';
  const filename = `tweetvia/${slug}-${Date.now()}.${inferredExt}`;
  try {
    const r = await sendMessageAsync<{ ok?: boolean; filename?: string; error?: string }>(id, { type: 'tweetvia:downloadImage', url, filename });
    if (!r?.ok) return null;
    return r.filename ?? filename;
  } catch { return null; }
}

/**
 * Read the last passive-capture attempt the extension recorded (if any). Returns the
 * raw object the background stored under `tv_lastCapture`. Useful for debugging from
 * the Engage tab without forcing users to open chrome://extensions.
 */
export async function readLastCaptureStatus(): Promise<{ ts: number; ok: boolean; status?: number; count?: number; reason?: string; monitorId?: string; query?: string } | null> {
  const id = getExtensionId();
  if (!id) return null;
  try {
    const r = await sendMessageAsync<{ last?: any }>(id, { type: 'tweetvia:lastCapture' }, 2000);
    return r?.last ?? null;
  } catch { return null; }
}
